import { generatePythonHeadless } from "../components/blocks/solutionTools";

const API = "/api";
const POLL_MS = 2000;
const MAX_WAIT_MS = 4 * 60 * 1000;

const readJson = async (r) => r.json().catch(() => ({}));
const detail = (d, fallback) => (typeof d?.detail === "string" ? d.detail : d?.detail?.message || fallback);

const STAGE_TEXT = {
  junior: { symmetry: "Mirroring and rotating the grid, solving each layout…", random: "Laying out new grids and solving them…" },
};

// Draft -> (Blocks: regenerate Python from the converted blocks) -> commit. `onStage(text)` reports progress.
// `strategy` (Junior only): "symmetry" = mirror / rotate the original grid, "random" = auto-solver re-lay.
export const generateVariations = async (question, { count = 5, strategy = "random", onStage = () => {} } = {}) => {
  onStage(STAGE_TEXT[question.mode]?.[strategy] || "Asking the AI for new versions and checking every solution…");
  const start = await fetch(`${API}/admin/questions/${question.question_id}/variations/draft`, {
    method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ count, strategy }),
  });
  const started = await readJson(start);
  if (!start.ok) throw new Error(detail(started, "Could not start generating variations"));

  const began = Date.now();
  let job;
  for (;;) {
    await new Promise((res) => setTimeout(res, POLL_MS));
    const r = await fetch(`${API}/admin/variations/jobs/${started.job_id}`, { credentials: "include" });
    job = await readJson(r);
    if (!r.ok) throw new Error(detail(job, "Lost track of the generation job"));
    if (job.status === "error") throw new Error(job.error || "Generation failed");
    if (job.status === "done") break;
    if (Date.now() - began > MAX_WAIT_MS) throw new Error("Generation is taking too long - please try again");
  }

  let variants = job.variants || [];
  const skipped = (job.skipped || []).map((reason) => ({ title: "", reason }));
  if (question.mode === "blocks") {
    onStage("Converting solutions to blocks…");
    const inputs = question.blocks?.inputs || [];
    variants = variants.filter((v) => {
      try {
        v.blocks.solution_python = generatePythonHeadless(v.blocks.solution_workspace, inputs);
        return true;
      } catch (e) {
        skipped.push({ title: v.title, reason: `Blocks could not be loaded: ${e.message}` });
        return false;
      }
    });
  }
  if (!variants.length) return { created: [], skipped, message: "No variations could be generated" };

  onStage("Verifying and saving…");
  const c = await fetch(`${API}/admin/questions/${question.question_id}/variations/commit`, {
    method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ variants }),
  });
  const committed = await readJson(c);
  if (!c.ok) throw new Error(detail(committed, "Could not save the variations"));
  return { created: committed.created || [], skipped: [...skipped, ...(committed.skipped || [])], message: committed.message };
};
